import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router';
import axios from 'axios';
import { map, get } from 'lodash';
import { RootState } from 'modules';
import QuizInfoSubModal from 'components/learning/quiz/components/QuizInfoSubModal';
import { handleFieldError } from 'libs/api/errorHandle';
import { ESubject, CSubject, TQuizConnectedExamples, TSteps, CQuizStep } from 'modules/learning';
import { TModalProps } from 'modules/types';
import { TModalState } from 'modules/types';
import { Checkbox, Form, Typography, Col, Row, Modal, Button, Input, InputNumber, message } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { FormWrapper } from 'GlobalStyles';

const { Title } = Typography;

const StyledSettingQuizInfoWrapper = styled.div`
  & .ant-form-item-label > label {
    width: 70px;
  }
`;

const EditQuizInfoModal = ({ modalState, setModalState }: TModalProps) => {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [editQuizSubModal, setEditQuizSubModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<{
    id: number | null;
    name: string | null;
  } | null>();
  const [selectedExample, setSelectedExample] = useState<TQuizConnectedExamples | null>();
  const [steps, setSteps] = useState<Array<TSteps>>();
  const { operation, id }: { operation: string; id: string } = useParams();
  const quizState = useSelector((store: RootState) => store.createQuizReducer);

  useEffect(() => {
    const quizStepData = async () => {
      const { data } = await axios.get('/data/quizStepData.json');
      setSteps(data);
    };
    quizStepData();
  }, []);

  //취소 눌렀을 때 => 아무 변화 없이 모달이 꺼짐
  function handleCancel() {
    setModalState({ visible: false, record: '' });
  }

  //확인 눌렀을 때
  function onClickSubmit() {
    form.validateFields().then((value) => {
      handleSubmit(value as any);
    });

    async function handleSubmit(value: any) {
      try {
        setSubmitButtonDisabled(true);

        //==> 필터링 관련 쿼리 만들기 위해 value 변수에 필터링 상태를 객체로 저장하고 onFinish의 인자로 전달하기
        const value = {
          quiz_number: 0,
          quiz_title: '',
          quiz_step: {},
          quiz_subject: '',
          quiz_group_id: null,
          connected_example_id: null,
        };

        //일단 프론트단에서 상태값 반영해야 하는 것 넘버, 제목, 스텝
        const quiz_number = Number(form.getFieldValue('quiz_number'));
        if (quiz_number) {
          value.quiz_number = quiz_number;
        }
        const quiz_title = form.getFieldValue('quiz_title');
        if (quiz_title) {
          value.quiz_title = quiz_title;
        }
        const quiz_step = form.getFieldValue('step');
        if (quiz_step) {
          value.quiz_step = quiz_step[0];
        }
        const quiz_subject = form.getFieldValue('subject');
        if (quiz_subject) {
          value.quiz_subject = quiz_subject[0];
        }
        const quiz_group = form.getFieldValue('quiz_group');
        if (quiz_group) {
          value.quiz_group_id = quiz_group[0];
        }
        const connected_example = form.getFieldValue('connected_example');
        if (connected_example) {
          value.connected_example_id = connected_example[0];
        }

        // ==> value 변수에 필터링 상태를 객체로 저장하고 onFinish의 인자로 전달하기
        modalState.onFinish && modalState.onFinish(value);

        handleCancel();
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  }

  const checkLimitNum = () => {
    const checkSubjectValue = form.getFieldValue('subject');
    if (checkSubjectValue !== undefined && checkSubjectValue.length > 1) {
      //체크안됩니다
      message.error('주제는 한 개만 체크할 수 있습니다');
    }
  };

  useEffect(() => {
    setVisible(modalState.visible);
  }, [modalState.visible]);

  useEffect(() => {
    if (id) {
      // quizState.quiz_group_id는 모달 열리고 닫힐 때마다 찐 모달 value에 따라 변하는데 quizState.quiz_group는 수정하는 문제 데이터 받아준 값으로 있기 때문에
      if (quizState.quiz_group?.id === quizState.quiz_group_id) {
        setSelectedGroup(quizState.quiz_group);
      }
      if (quizState.example?.id === quizState.example_id) {
        setSelectedExample(quizState.example);
      }
      //연결된 예제 없으면 null로 들어오기 때문에
      if (quizState.example_id) {
        form.setFields([{ name: 'connected_example', value: [quizState.example_id] }]);
      }
      //그룹 없으면 null 로 들어오기때문에
      if (quizState.quiz_group_id) {
        form.setFields([{ name: 'quiz_group', value: [quizState.quiz_group_id] }]);
      }
      form.setFields([
        { name: 'quiz_number', value: quizState.quiz_number },
        { name: 'quiz_title', value: quizState.title },
        { name: 'subject', value: [quizState.subject] },
        {
          name: 'step',
          value: steps && [...steps].filter((el: any) => el.step_name === quizState.quiz_type),
        },
      ]);
    }
  }, [modalState]);

  return (
    <Modal
      visible={visible}
      centered={true}
      maskClosable={true}
      closable={true}
      footer={
        <Row gutter={[8, 8]} justify='space-between'>
          <Col>
            <Button
              onClick={() => {
                form.resetFields();
              }}
            >
              <RedoOutlined />
              초기화
            </Button>
          </Col>
          <Col>
            <Button onClick={handleCancel}>닫기</Button>
            <Button type='primary' onClick={onClickSubmit} loading={submitButtonDisabled}>
              적용
            </Button>
          </Col>
        </Row>
      }
      width={540}
      onCancel={handleCancel}
    >
      <FormWrapper style={{ padding: '0px' }}>
        {modalState.type === 'settingQuizInfo' && (
          <StyledSettingQuizInfoWrapper>
            <Title level={3}>문제 기본정보 편집</Title>

            <Form
              form={form}
              name='QuizInfoModal'
              colon={false}
              // layout="vertical"
              className='form-normal'
              style={{ margin: '10px' }}
            >
              <Form.Item
                name={'quiz_number'}
                label='문제번호'
                className='form-item'
                rules={[{ required: true, message: 'Please input' }]}
              >
                <InputNumber size='large' autoFocus={true} placeholder='문제 번호를 입력해주세요(숫자)' />
              </Form.Item>
              <Form.Item
                name={'quiz_title'}
                label='문제이름'
                className='form-item'
                rules={[{ required: true, message: 'Please input' }]}
              >
                <Input size='large' placeholder='문제 이름을 입력해주세요' />
              </Form.Item>
              <Form.Item name={'subject'} label='주제' rules={[{ required: true, message: 'Please check' }]}>
                <Checkbox.Group className='checkbox-btn' onChange={checkLimitNum}>
                  <Checkbox value={ESubject.FitInterview}>{CSubject[ESubject.FitInterview]}</Checkbox>
                  <Checkbox value={ESubject.CaseInterview}>{CSubject[ESubject.CaseInterview]}</Checkbox>
                  <Checkbox value={ESubject.TurnaroundStrategy}>
                    {CSubject[ESubject.TurnaroundStrategy]}
                  </Checkbox>
                  <Checkbox value={ESubject.GrowthStrategy}>{CSubject[ESubject.GrowthStrategy]}</Checkbox>
                  <Checkbox value={ESubject.DdMarketSizing}>{CSubject[ESubject.DdMarketSizing]}</Checkbox>
                  <Checkbox value={ESubject.OperationOptimization}>
                    {CSubject[ESubject.OperationOptimization]}
                  </Checkbox>
                  <Checkbox value={ESubject.SalesMarketingStrategy}>
                    {CSubject[ESubject.SalesMarketingStrategy]}
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item style={{ marginBottom: '5px' }} label='문제그룹'>
                <Button
                  style={{ float: 'none', marginRight: '10px' }}
                  onClick={() => {
                    setEditQuizSubModal({
                      record: form.getFieldValue('quiz_group'),
                      type: 'quizGroup',
                      visible: true,
                      //서브모달 닫을 때 서브모달의 form 벨류를 받아서 form
                      onFinish: (data: any) => {
                        // console.log('그룹 데이터', data);
                        if (data.quiz_group) {
                          setSelectedGroup(data.quiz_group);
                          form.setFields([{ name: 'quiz_group', value: [data.quiz_group.id] }]);
                        } else {
                          setSelectedGroup(null);
                          form.setFields([{ name: 'quiz_group', value: [] }]);
                        }
                      },
                    });
                  }}
                >
                  선택
                </Button>
                <Form.Item name={'quiz_group'} style={{ display: 'inline-block' }}>
                  {form.getFieldValue('quiz_group') && selectedGroup && (
                    <Checkbox.Group className='checkbox-btn'>
                      <Checkbox
                        value={selectedGroup?.id}
                        defaultChecked={true}
                        // checked={true}
                      >
                        {selectedGroup?.name}
                        {/* {form.getFieldValue('quiz_group')} */}
                      </Checkbox>
                    </Checkbox.Group>
                  )}
                </Form.Item>
              </Form.Item>
              <Form.Item name={'step'} label='단계' rules={[{ required: true, message: 'Please check' }]}>
                <Checkbox.Group className='checkbox-btn'>
                  {map(steps, (v) => (
                    <Checkbox key={get(v, 'name')} value={v}>
                      {CQuizStep[v.step_name]}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
              <Form.Item style={{ marginBottom: '5px' }} label='연결된예제'>
                <Button
                  style={{ float: 'none', marginRight: '10px' }}
                  onClick={() => {
                    setEditQuizSubModal({
                      record: form.getFieldValue('connected_example'),
                      type: 'connectedExample',
                      visible: true,
                      //서브모달 닫을 때 서브모달의 form 벨류를 받아서 form
                      onFinish: (data: any) => {
                        // console.log('data', data);
                        if (data.connected_example) {
                          setSelectedExample(data.connected_example);
                          form.setFields([
                            {
                              name: 'connected_example',
                              value: [data.connected_example.id],
                            },
                          ]);
                        } else {
                          setSelectedExample(null);
                          form.setFields([{ name: 'connected_example', value: [] }]);
                        }
                      },
                    });
                  }}
                >
                  선택
                </Button>
                <Form.Item name={'connected_example'} style={{ display: 'inline-block' }}>
                  {form.getFieldValue('connected_example') && selectedExample && (
                    <Checkbox.Group className='checkbox-btn'>
                      <Checkbox value={selectedExample?.id} defaultChecked={true}>
                        {selectedExample?.subject} <br /> {selectedExample?.title}
                        {/* {form.getFieldValue('connected_example')} */}
                      </Checkbox>
                    </Checkbox.Group>
                  )}
                </Form.Item>
              </Form.Item>
            </Form>
            <QuizInfoSubModal modalState={editQuizSubModal} setModalState={setEditQuizSubModal} />
          </StyledSettingQuizInfoWrapper>
        )}
      </FormWrapper>
    </Modal>
  );
};

EditQuizInfoModal.defaultProps = {};

export default EditQuizInfoModal;
